import { FormikAutoComplete } from 'components/forms/formik/FormikAutoComplete';
import { FormikCheckbox } from 'components/forms/formik/FormikCheckbox';
import { FormikCompanySearch } from 'components/forms/formik/FormikCompanySearch';
import { FormikColorPicker } from 'components/forms/formik/FormikColorPicker';
import { FormikDocumentSectionUploader } from 'components/forms/formik/FormikDocumentSectionsUploader';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikDynamicArray } from 'components/forms/formik/FormikDynamicArray';
import { FormikGoogleLocation } from 'components/forms/formik/FormikGoogleLocation';
import { FormikIbanInput } from 'components/forms/formik/FormikIbanInput';
import { FormikUploadImage } from 'components/forms/formik/FormikImageUploader';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikInputWithUnit } from 'components/forms/formik/FormikInputWithUnit';
import { FormikNumberSelector } from 'components/forms/formik/FormikNumberSelector';
import { FormikRadioGroup } from 'components/forms/formik/FormikRadioGroup';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { FormikTagInput } from 'components/forms/formik/FormikTagInput';
import { FormikNumberWithVariableUnit } from 'components/forms/formik/FormikNumberWithVariableUnit';
import { InputWithUnit } from 'components/forms/fragments/InputWithUnit';
import { ComponentKey } from 'enums/componentKey';
import { DynamicArrayInputField } from 'types/form/componentConfig';
import { InputRenderersFor, RowFieldContext, RowInputRenderersFor, translateItems } from './types';

type SocleKey =
  | ComponentKey.InputText
  | ComponentKey.InputWithUnit
  | ComponentKey.Dropdown
  | ComponentKey.Checkbox
  | ComponentKey.Switch
  | ComponentKey.RadioGroup
  | ComponentKey.CompanySearch
  | ComponentKey.Iban
  | ComponentKey.GoogleLocation
  | ComponentKey.DynamicArray
  | ComponentKey.NumberSelector
  | ComponentKey.ImageUpload
  | ComponentKey.ColorPicker
  | ComponentKey.TagInput
  | ComponentKey.AutoComplete
  | ComponentKey.NumberWithVariableUnit
  | ComponentKey.DocumentSectionsUploader
  | ComponentKey.Custom;

/* The CORE registry: only the generic components of components/forms/formik.
   Feature keys (referentials, mandate…) are contributed by their features and
   merged at the composition root (src/index.tsx) — never imported here. */
export const socleRenderers: InputRenderersFor<SocleKey> = {
  [ComponentKey.InputText]: (cfg, base) => (
    <FormikInputText
      {...base}
      type={cfg.type}
      min={cfg.min}
      max={cfg.max}
      step={cfg.step}
      existenceCheck={cfg.existenceCheck}
    />
  ),

  [ComponentKey.InputWithUnit]: (cfg, base, { t }) => (
    <FormikInputWithUnit
      {...base}
      className={cfg.className}
      labelTooltip={cfg.labelTooltip ? t(cfg.labelTooltip) : undefined}
      type={cfg.type}
      min={cfg.min}
      max={cfg.max}
      step={cfg.step}
      unit={cfg.unit}
      variantSize={cfg.variantSize}
      groupThousands={cfg.groupThousands}
    />
  ),

  [ComponentKey.Dropdown]: (cfg, base, { t }) => (
    <FormikDropdown {...base} items={translateItems(cfg.items, t)} />
  ),

  [ComponentKey.Checkbox]: (_cfg, base) => (
    <FormikCheckbox name={base.name} label={base.label} disabled={base.disabled} />
  ),

  [ComponentKey.Switch]: (cfg, base, { t }) => (
    <FormikSwitch
      name={base.name}
      label={base.label}
      falseLabel={cfg.falseLabel ? t(cfg.falseLabel) : undefined}
      disabled={base.disabled}
    />
  ),

  [ComponentKey.RadioGroup]: (cfg, base, { t }) => (
    <FormikRadioGroup
      name={base.name}
      label={base.label}
      options={translateItems(cfg.options, t)}
      required={base.required}
      disabled={base.disabled}
      direction={cfg.direction}
    />
  ),

  [ComponentKey.CompanySearch]: (_cfg, base) => <FormikCompanySearch {...base} />,

  [ComponentKey.Iban]: (_cfg, base) => <FormikIbanInput {...base} />,

  [ComponentKey.GoogleLocation]: (cfg, base) => (
    <FormikGoogleLocation {...base} fieldPrefix={cfg.fieldPrefix} />
  ),

  [ComponentKey.DynamicArray]: (cfg, base, { t }) => (
    <FormikDynamicArray
      {...base}
      gridCols={cfg.gridCols}
      fields={cfg.fields.map(field => ({
        ...field,
        label: field.label ? t(field.label) : field.label,
        placeholder: field.placeholder ? t(field.placeholder) : undefined,
      }))}
      minItems={cfg.minItems}
      maxItems={cfg.maxItems}
      addButtonText={cfg.addButtonText ? t(cfg.addButtonText) : undefined}
      initialItem={cfg.initialItem}
      emptyConfig={cfg.emptyConfig ? {
        ...cfg.emptyConfig,
        title: t(cfg.emptyConfig.title),
        description: cfg.emptyConfig.description ? t(cfg.emptyConfig.description) : undefined,
      } : undefined}
      compact={cfg.compact}
    />
  ),

  [ComponentKey.NumberSelector]: (cfg, base) => {
    const validateNumber = (value: unknown): number | undefined =>
      Number.isFinite(value) ? value as number : undefined;
    const min = validateNumber(cfg.min);
    const max = validateNumber(cfg.max);
    if (min !== undefined && max !== undefined && min > max) {
      console.warn(`NumberSelector "${cfg.name}": Invalid range (min: ${min}, max: ${max})`);
    }
    return (
      <FormikNumberSelector
        {...base}
        min={min}
        max={max}
        step={cfg.step}
        unit={cfg.unit}
        unitPosition={cfg.unitPosition}
        variant={cfg.variant}
        values={cfg.values}
        defaultValue={cfg.defaultValue}
      />
    );
  },

  /* `label ?? ''` keeps the historical rendering: a config without label
     shows no heading (ImageUpload would otherwise default to "Photo"). */
  [ComponentKey.ImageUpload]: (cfg, base, { t }) => (
    <FormikUploadImage
      fieldName={base.name}
      label={base.label ?? ''}
      placeholder={base.placeholder ?? cfg.placeholder}
      dragPlaceholder={t(cfg.dragPlaceholder)}
      required={base.required}
      height={cfg.height}
      maxSizeInMB={cfg.maxSizeInMB}
      acceptedTypes={cfg.acceptedTypes}
      className={cfg.className}
      previewUrl={cfg.previewUrl}
    />
  ),

  [ComponentKey.ColorPicker]: (_cfg, base) => (
    <FormikColorPicker
      name={base.name}
      label={base.label}
      required={base.required}
      disabled={base.disabled}
    />
  ),

  [ComponentKey.TagInput]: (cfg, base, { t }) => (
    <FormikTagInput {...base} items={translateItems(cfg.items, t)} maxTags={cfg.maxTags} />
  ),

  [ComponentKey.AutoComplete]: (cfg, base, { t }) => (
    <FormikAutoComplete
      {...base}
      autocompleteKey={cfg.autocompleteKey}
      noResultsText={cfg.noResultsText ? t(cfg.noResultsText) : undefined}
      initialLabel={cfg.initialLabel}
    />
  ),

  [ComponentKey.NumberWithVariableUnit]: (cfg, base, { t }) => (
    <FormikNumberWithVariableUnit
      {...base}
      units={translateItems(cfg.units, t)}
      min={cfg.min}
      max={cfg.max}
      step={cfg.step}
      className={cfg.className}
    />
  ),

  [ComponentKey.DocumentSectionsUploader]: (cfg, base, { t }) => (
    <FormikDocumentSectionUploader
      label={base.label}
      sections={cfg.sections}
      completedSectionLabel={cfg.completedSectionLabel ? t(cfg.completedSectionLabel) : undefined}
      disabledSectionLabel={cfg.disabledSectionLabel ? t(cfg.disabledSectionLabel) : undefined}
      emptyMessage={cfg.emptyMessage ? t(cfg.emptyMessage) : undefined}
      acceptedFormats={cfg.acceptedFormats}
      disabled={base.disabled}
    />
  ),

  /* Custom fields are rendered by hand in their layout: the factory renders
     nothing, as before. */
  [ComponentKey.Custom]: () => <></>,
};

/* Core row renderers for DynamicArray: plain text/number inputs. The row
   parses the event itself so the array only ever sees a value. */
const parseInput = (type: 'text' | 'number' | undefined, raw: string) =>
  type === 'number' ? (raw === '' ? '' : Number(raw)) : raw;

const textRow = (field: DynamicArrayInputField, { value, onChange, label, required }: RowFieldContext) => (
  <InputWithUnit
    label={label}
    required={required}
    name={field.name}
    type={field.type || 'text'}
    value={value}
    onChange={e => onChange(parseInput(field.type, e.target.value))}
    placeholder={field.placeholder}
    unit={field.unit || ''}
  />
);

export const socleRowRenderers: RowInputRenderersFor<ComponentKey.InputText | ComponentKey.InputWithUnit> = {
  [ComponentKey.InputText]: textRow,
  [ComponentKey.InputWithUnit]: textRow,
};
